<script setup>
import { ref, onMounted, watch } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

// Reactive data
const workOrders = ref([]);
const filteredBreakdowns = ref([]);
const assets = ref([]);
const loading = ref(false);
const error = ref(null);
const notification = ref({
  show: false,
  message: '',
  type: ''
});

// Filter options
const filterOptions = ref({
  selectedAsset: '',
  fromDate: '',
  toDate: ''
});

// API endpoints
const workOrdersApiUrl = `${API_BASE_URL}/api/workorders/list`;
const assetsApiUrl = `${API_BASE_URL}/api/assets`;

// Fetch assets data
const fetchAssets = async () => {
  try {
    const response = await axios.get(assetsApiUrl);
    if (Array.isArray(response.data)) {
      assets.value = response.data;
    }
  } catch (err) {
    console.error('Error fetching assets:', err);
    showNotification('Error loading assets data', 'error');
  }
};

// Fetch all work orders and filter breakdowns
const fetchWorkOrders = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await axios.get(workOrdersApiUrl);
    workOrders.value = response.data;
    
    // Apply initial filters
    applyFilters();
    
  } catch (err) {
    console.error('Error fetching work orders:', err);
    error.value = 'Failed to load work orders.';
    showNotification(error.value, 'error');
    workOrders.value = [];
    filteredBreakdowns.value = [];
  } finally {
    loading.value = false;
  }
};

// Detect breakdown work orders - WorkStatus = 2
const isBreakdownWorkOrder = (wo) => {
  return wo.WorkStatus === 2;
};

// Filter only breakdown work orders
const applyFilters = () => {
  let filtered = workOrders.value;

  // Filter breakdown work orders (WorkStatus = 2)
  const breakdowns = filtered.filter(wo => isBreakdownWorkOrder(wo));

  // Filter by selected asset
  if (filterOptions.value.selectedAsset) {
    filtered = breakdowns.filter(wo => wo.AssetNo === filterOptions.value.selectedAsset);
  } else {
    filtered = breakdowns;
  }

  // Filter by date range on Start Date
  if (filterOptions.value.fromDate || filterOptions.value.toDate) {
    filtered = filtered.filter(wo => {
      const startDate = new Date(wo.StartDate);
      let fromCondition = true;
      let toCondition = true;

      if (filterOptions.value.fromDate) {
        const fromDate = new Date(filterOptions.value.fromDate);
        fromCondition = startDate >= fromDate;
      }

      if (filterOptions.value.toDate) {
        const toDate = new Date(filterOptions.value.toDate);
        toDate.setHours(23, 59, 59, 999);
        toCondition = startDate <= toDate;
      }

      return fromCondition && toCondition;
    });
  }

  filteredBreakdowns.value = filtered;
};

// Clear all filters
const clearFilters = () => {
  filterOptions.value.selectedAsset = '';
  filterOptions.value.fromDate = '';
  filterOptions.value.toDate = '';
  applyFilters();
};

// Watch for filter changes
watch([
  () => filterOptions.value.selectedAsset,
  () => filterOptions.value.fromDate,
  () => filterOptions.value.toDate
], () => {
  applyFilters();
});

// Show notification helper
const showNotification = (message, type) => {
  notification.value.message = message;
  notification.value.type = type;
  notification.value.show = true;
  setTimeout(() => {
    notification.value.show = false;
  }, 5000);
};

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return date.toLocaleDateString(undefined, options);
};

// Get asset description/name
const getAssetDescription = (assetNo) => {
  const asset = assets.value.find(a => a.asset_no === assetNo);
  return asset ? asset.asset_desc : assetNo;
};

// Get selected asset description for display
const getSelectedAssetDescription = () => {
  if (!filterOptions.value.selectedAsset) return 'All Assets';
  const asset = assets.value.find(a => a.asset_no === filterOptions.value.selectedAsset);
  return asset ? `${asset.asset_no} (${asset.asset_desc})` : filterOptions.value.selectedAsset;
};

// Format asset display for dropdown
const formatAssetDisplay = (asset) => {
  if (asset.asset_no && asset.asset_desc) {
    return `${asset.asset_no} (${asset.asset_desc})`;
  } else if (asset.asset_no) {
    return asset.asset_no;
  } else {
    return 'Unknown Asset';
  }
};

// Prepare chart data for dot plot
const prepareChartData = () => {
  if (filteredBreakdowns.value.length === 0) {
    return [];
  }

  // Group by asset and date
  const assetDates = {};
  
  filteredBreakdowns.value.forEach(breakdown => {
    const assetKey = breakdown.AssetNo;
    const date = new Date(breakdown.StartDate).toISOString().split('T')[0];
    
    if (!assetDates[assetKey]) {
      assetDates[assetKey] = {
        assetNo: assetKey,
        assetDesc: getAssetDescription(assetKey),
        dates: new Set()
      };
    }
    
    assetDates[assetKey].dates.add(date);
  });

  // Convert to array and sort assets
  return Object.values(assetDates)
    .map(item => ({
      ...item,
      dates: Array.from(item.dates).sort()
    }))
    .sort((a, b) => a.assetDesc.localeCompare(b.assetDesc));
};

// Print PDF with the chart
const printPDF = () => {
  if (filteredBreakdowns.value.length === 0) {
    showNotification('No breakdown data to print.', 'warning');
    return;
  }
  
  const chartData = prepareChartData();
  const allDates = [...new Set(chartData.flatMap(item => item.dates))].sort();
  
  const printData = {
    title: 'Asset Breakdown Analysis',
    filters: {
      asset: getSelectedAssetDescription(),
      fromDate: filterOptions.value.fromDate ? formatDate(filterOptions.value.fromDate) : '',
      toDate: filterOptions.value.toDate ? formatDate(filterOptions.value.toDate) : ''
    },
    chartData: chartData,
    allDates: allDates,
    totalBreakdowns: filteredBreakdowns.value.length
  };
  
  const printWindow = window.open('', '_blank');
  
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Asset Breakdown Analysis</title>
      <style>
        body { 
          font-family: Arial, sans-serif; 
          margin: 20px; 
          background: white;
        }
        .print-header { 
          display: flex; 
          justify-content: space-between; 
          align-items: center; 
          margin-bottom: 30px; 
          border-bottom: 2px solid #333; 
          padding-bottom: 10px; 
        }
        .logo-img { 
          max-height: 80px; 
          max-width: 150px; 
        }
        .logo-img2 { 
          max-height: 120px; 
          max-width: 120px; 
        }
        .header-center { 
          flex: 2; 
          text-align: center; 
        }
        .print-title { 
          font-size: 24px; 
          font-weight: bold; 
          margin-bottom: 10px; 
          color: #333;
        }
        .print-filters { 
          margin-bottom: 20px; 
          font-size: 14px; 
          color: #666; 
        }
        .summary { 
          margin: 20px 0; 
          padding: 15px; 
          background: #f8f9fa; 
          border-radius: 5px; 
          text-align: center;
        }
        .chart-container {
          margin: 30px 0;
          overflow-x: auto;
        }
        .chart-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .chart-table th {
          background-color: #f5f5f5;
          padding: 10px;
          text-align: center;
          border: 1px solid #ddd;
          font-weight: bold;
        }
        .chart-table td {
          padding: 10px;
          text-align: center;
          border: 1px solid #ddd;
          vertical-align: middle;
        }
        .asset-name {
          text-align: left;
          font-weight: bold;
          white-space: nowrap;
          min-width: 200px;
        }
        .dot {
          display: inline-block;
          width: 12px;
          height: 12px;
          background-color: #dc3545;
          border-radius: 50%;
          margin: 0 2px;
        }
        .date-header {
          writing-mode: vertical-lr;
          transform: rotate(180deg);
          white-space: nowrap;
          padding: 10px 5px;
        }
        /* Print-specific styles */
        @media print {
          body { margin: 0; }
          .print-header { margin-bottom: 20px; }
          .chart-table { font-size: 10px; }
          .asset-name { min-width: 150px; }
          .dot {
            background-color: #dc3545 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            border: 1px solid #000 !important;
          }
        }
        /* Fallback for browsers that don't support print-color-adjust */
        .dot-print {
          color: #dc3545;
          font-size: 16px;
          line-height: 1;
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <div class="logo-left">
          <img src="../logo.png" alt="Company Logo Left" class="logo-img">
        </div>
        <div class="header-center">
          <div class="print-title">Asset Breakdown Analysis</div>
          <div class="print-filters">
            Asset: ${printData.filters.asset} | 
            Date Range: ${printData.filters.fromDate} to ${printData.filters.toDate} | 
            Report Date: ${new Date().toLocaleDateString()} | 
            Total Breakdowns: ${printData.totalBreakdowns}
          </div>
        </div>
        <div class="logo-right">
          <img src="../logo2.png" alt="Company Logo Right" class="logo-img2">
        </div>
      </div>

      <div class="summary">
        <strong>Breakdown Analysis Chart</strong><br>
        Showing ${printData.totalBreakdowns} breakdown(s) across ${printData.chartData.length} asset(s)
      </div>

      <div class="chart-container">
        <table class="chart-table">
          <thead>
            <tr>
              <th class="asset-name">Asset</th>
              ${printData.allDates.map(date => `
                <th class="date-header">${formatDate(date)}</th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${printData.chartData.map(asset => `
              <tr>
                <td class="asset-name"> (${asset.assetNo})</td>
                ${printData.allDates.map(date => `
                  <td>
                    ${asset.dates.includes(date) ? 
                      '●' :  // Using Unicode character as fallback
                      ''}
                  </td>
                `).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div style="margin-top: 30px; text-align: center; color: #666; font-size: 12px;">
        <div style="display: inline-block; margin-right: 20px;">
          <span style="color: #dc3545; font-size: 16px; vertical-align: middle;">●</span>
          <span style="vertical-align: middle; margin-left: 5px;">= Breakdown</span>
        </div>
        <div style="display: inline-block;">
          <span style="vertical-align: middle;">Empty = No Breakdown</span>
        </div>
      </div>

      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => {
            window.close();
          }, 1000);
        };
      <\/script>
    </body>
    </html>
  `);
  printWindow.document.close();
};

// Initialize component
onMounted(() => {
  fetchAssets();
  fetchWorkOrders();
});
</script>

<template>
  <div class="container mt-4">
    
    <h2 class="report-title">Asset Breakdown Analysis</h2>

    <!-- Asset and Date Filters -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="card-title mb-0">Filter Options</h5>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <!-- Asset Dropdown -->
          <div class="col-md-4">
            <label class="form-label">Select Asset</label>
            <select class="form-select" v-model="filterOptions.selectedAsset">
              <option value="">All Assets</option>
              <option 
                v-for="asset in assets" 
                :key="asset.asset_no" 
                :value="asset.asset_no"
              >
                {{ formatAssetDisplay(asset) }}
              </option>
            </select>
            <div v-if="assets.length === 0" class="text-warning small mt-1">
              Loading assets...
            </div>
          </div>
          
          <!-- Date Range Filters -->
          <div class="col-md-2">
            <label class="form-label">From Date</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="filterOptions.fromDate"
              :max="filterOptions.toDate || new Date().toISOString().split('T')[0]"
            >
          </div>
          <div class="col-md-2">
            <label class="form-label">To Date</label>
            <input 
              type="date" 
              class="form-control" 
              v-model="filterOptions.toDate"
              :min="filterOptions.fromDate"
              :max="new Date().toISOString().split('T')[0]"
            >
          </div>
          
          <!-- Actions and Counter -->
          <div class="col-md-4 d-flex align-items-end gap-2">
            <button @click="clearFilters" class="btn btn-outline-secondary">
              Clear Filters
            </button>
            <div class="text-muted ms-2">
              {{ filteredBreakdowns.length }} breakdown(s) found
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="text-muted">
        <span v-if="filterOptions.selectedAsset">
          Showing breakdowns for: <strong>{{ getSelectedAssetDescription() }}</strong>
        </span>
        <span v-else>Showing breakdowns for all assets</span>
        <span v-if="filterOptions.fromDate || filterOptions.toDate" class="ms-2">
          (Date range: 
          <span v-if="filterOptions.fromDate">{{ formatDate(filterOptions.fromDate) }}</span>
          <span v-if="filterOptions.fromDate && filterOptions.toDate"> to </span>
          <span v-if="filterOptions.toDate">{{ formatDate(filterOptions.toDate) }}</span>)
        </span>
      </div>
      <div class="report-actions" v-if="filteredBreakdowns.length > 0 && !loading">
        <button @click="printPDF" class="btn btn-secondary">
          <i class="bi bi-printer"></i> Print as PDF
        </button>
      </div>
    </div>

    <!-- Loading & Error States -->
    <div v-if="loading" class="alert alert-info">
      <i class="bi bi-arrow-repeat spinner"></i> Loading breakdown data...
    </div>
    <div v-if="error" class="alert alert-danger">
      {{ error }}
    </div>
    <div v-if="notification.show" :class="['alert', `alert-${notification.type}`]">
      {{ notification.message }}
    </div>

    <!-- Dot Chart Visualization -->
    <div v-if="!loading && !error && filteredBreakdowns.length > 0" class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">Breakdown Timeline Chart</h5>
      </div>
      <div class="card-body">
        <div class="chart-preview">
          <div class="table-responsive">
            <table class="table table-bordered chart-table-preview">
              <thead>
                <tr>
                  <th class="asset-column">Asset</th>
                  <th 
                    v-for="date in [...new Set(filteredBreakdowns.map(b => new Date(b.StartDate).toISOString().split('T')[0]))].sort()" 
                    :key="date"
                    class="date-column"
                  >
                    {{ formatDate(date) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="assetData in prepareChartData()" :key="assetData.assetNo">
                  <td class="asset-column">
                    <strong>{{ assetData.assetDesc }}</strong>
                    <br>
                    <small class="text-muted">{{ assetData.assetNo }}</small>
                  </td>
                  <td 
                    v-for="date in [...new Set(filteredBreakdowns.map(b => new Date(b.StartDate).toISOString().split('T')[0]))].sort()" 
                    :key="date"
                    class="date-column text-center"
                  >
                    <span 
                      v-if="assetData.dates.includes(date)" 
                      class="dot-preview"
                      :title="`Breakdown on ${formatDate(date)}`"
                    ></span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- No Data Message -->
    <div v-if="!loading && !error && filteredBreakdowns.length === 0" class="alert alert-warning text-center">
      <i class="bi bi-graph-down"></i><br>
      <span v-if="filterOptions.selectedAsset || filterOptions.fromDate || filterOptions.toDate">
        No breakdowns found for the selected filters.
      </span>
      <span v-else>
        No breakdown work orders found in the system.
      </span>
    </div>
  </div>
</template>

<style scoped>
.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.chart-table-preview {
  font-size: 12px;
}

.asset-column {
  min-width: 200px;
  max-width: 200px;
  white-space: normal;
}

.date-column {
  min-width: 80px;
  text-align: center;
  vertical-align: middle;
}

.dot-preview {
  display: inline-block;
  width: 10px;
  height: 10px;
  background-color: #dc3545;
  border-radius: 50%;
  cursor: pointer;
}

.chart-table-preview th {
  background-color: #f8f9fa;
  font-weight: bold;
  text-align: center;
  vertical-align: middle;
}

.chart-preview {
  max-height: 600px;
  overflow: auto;
}
@media print {
  .dot {
    background-color: #dc3545 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    border: 1px solid #000 !important;
  }
}

</style>